import { useMemo, useState } from "react";
import useCoolPoll from "@hooks/useCoolPoll";
import { useStoreActions, useStoreState } from "../../models/RootStore";
import {
  getSiteBucket,
  getSitesRequiringAttention,
  shouldShowPopup,
  SitePackageThresholdBucket,
  sortByUrgency,
  toSeenValue,
} from "../../utils/SitePackagesUtils";

const POLL_INTERVAL_MS = 12 * 60 * 60 * 1000;

interface IPendingSite {
  siteId: string;
  bucket: SitePackageThresholdBucket;
}

/**
 * Drives the site-packages expiration popup: one window listing every pending site, most urgent first.
 *
 * Mirrors the renewal email: only sites the BE flags with showRenewalNotice for this user. A dated
 * site is pending when it sits in a threshold bucket different from the last one the user
 * acknowledged (tighter threshold crossed, or package renewed); a site without an end date comes
 * back weekly until one is set. The top site filter is ignored on purpose, so a filtered view never hides a
 * notification about another site. Package data is re-fetched every 12 hours so an app left open
 * still gets the popup.
 */
export const useSitePackagesPopup = (enabled: boolean) => {
  const sitesPackages = useStoreState(state => state.sites.sitesPackages);
  const allSites = useStoreState(state => state.sites.allSites);
  const seenThresholds = useStoreState(
    state => state.users.userPreferences?.sitePackagesSeenThresholds || {}
  );
  const getSitesPackages = useStoreActions(actions => actions.sites.getSitesPackages);
  const updateSitePackagesSeenThresholds = useStoreActions(
    actions => actions.users.updateSitePackagesSeenThresholds
  );

  // Closed this session; hides the popup right away while the preference save is in flight.
  const [dismissed, setDismissed] = useState<{ [siteId: string]: string }>({});

  // Beyond the 12-hour cadence, useCoolPoll also refreshes when the tab becomes visible again,
  // so a tab left in the background doesn't wait another full interval after the user returns.
  useCoolPoll({
    enabled,
    sendInitalPolling: true,
    frequency: POLL_INTERVAL_MS,
    stopAfter: Infinity,
    inactivityThreshold: Infinity,
    doublePeriod: Infinity,
    onPoll: () => {
      getSitesPackages().catch(() => undefined);
    },
  });

  const pending = useMemo<IPendingSite[]>(() => {
    if (!enabled || !sitesPackages) {
      return [];
    }

    const attentionSites = sortByUrgency(getSitesRequiringAttention(sitesPackages, allSites));

    return attentionSites.reduce<IPendingSite[]>((acc, site) => {
      if (!site.showRenewalNotice) {
        return acc;
      }
      const bucket = getSiteBucket(site);
      const lastShown = dismissed[site.siteId] ?? seenThresholds[site.siteId];
      if (bucket && shouldShowPopup(bucket, lastShown)) {
        acc.push({ siteId: site.siteId, bucket });
      }
      return acc;
    }, []);
  }, [enabled, sitesPackages, allSites, seenThresholds, dismissed]);

  const siteIds = useMemo(() => pending.map(site => site.siteId), [pending]);

  const onClose = () => {
    if (pending.length === 0) {
      return;
    }
    const now = Date.now();
    const shown = pending.reduce<{ [siteId: string]: string }>(
      (acc, { siteId, bucket }) => ({ ...acc, [siteId]: toSeenValue(bucket, now) }),
      {}
    );
    setDismissed(prev => ({ ...prev, ...shown }));
    updateSitePackagesSeenThresholds(shown).catch(() => undefined);
  };

  return { siteIds, onClose };
};
